import React from 'react';
import { Link } from '../../../../components/Link';
import { Thumbnail } from '../../../../components/thumbnail/thumbnail';
import { SvgCheck, SvgPlayCircle } from '../../../../icons';
import { ImageProps } from '../../../../interfaces';
import { BookmarkedSingleRecording } from '../../../../queries/ats/recordings/types/recording';
import { EpisodeAsset } from '../../../../queries/ats/titles/types/asset';
import { BookmarkedSource } from '../../../../queries/ats/titles/types/sources';
import { routes } from '../../../../routes/route-utils';
import { getPercent } from '../../../../utils/commonUtils';

interface BaseBookmarkedThumbnailProps {
  image: ImageProps;
}

interface BookmarkedProgramThumbnailProps extends BaseBookmarkedThumbnailProps {
  episode: EpisodeAsset;
  source: BookmarkedSource;
}

interface BookmarkedRecordingThumbnailProps extends BaseBookmarkedThumbnailProps {
  recording: BookmarkedSingleRecording;
}

export type BookmarkedThumbnailProps = BookmarkedProgramThumbnailProps | BookmarkedRecordingThumbnailProps;

export function BookmarkedThumbnail(props: BookmarkedThumbnailProps) {
  if ('source' in props) {
    return <BookmarkedSourceThumbnail {...props} />;
  }

  return <BookmarkedRecordingThumbnail {...props} />;
}

export function BookmarkedRecordingThumbnail({ recording, image }: BookmarkedRecordingThumbnailProps) {
  const url = routes.recording(recording);

  if (recording.bookmark.isWatched) {
    return (
      <Thumbnail
        image={image}
        icon={SvgCheck}
        interactions={{
          backdrop: 'always',
          icon: 'always',
        }}
      />
    );
  }

  const progress =
    recording.bookmark !== undefined && recording.duration
      ? getPercent(recording.bookmark.position, recording.duration)
      : undefined;

  return (
    <Link to={url}>
      <Thumbnail
        image={image}
        icon={SvgPlayCircle}
        progress={progress}
        interactions={{
          backdrop: 'hover',
          icon: 'hover',
        }}
      />
    </Link>
  );
}

export function BookmarkedSourceThumbnail({ episode, source, image }: BookmarkedProgramThumbnailProps) {
  const isWatched = source.bookmark !== undefined && source.bookmark.isWatched;
  const progress = episode.duration ? getPercent(source.bookmark.position, episode.duration) : undefined;

  if (isWatched) {
    return (
      <Thumbnail
        image={image}
        icon={SvgCheck}
        interactions={{
          backdrop: 'always',
          icon: 'always',
        }}
      />
    );
  }

  return (
    <Link
      to={routes.play({
        platformTitleId: source.platformTitleId,
        providerIdentifier: source.providerIdentifier,
        position: progress,
      })}
    >
      <Thumbnail image={image} icon={SvgPlayCircle} progress={progress} interactions={undefined} />
    </Link>
  );
}
